import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';

const DatenschutzNeu: React.FC = () => {
  useEffect(() => {
    // Add meta robots tag to prevent indexing
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    document.head.appendChild(metaRobots);

    // Clean up function to remove the meta tag when component unmounts
    return () => {
      const existingTag = document.querySelector('meta[name="robots"]');
      if (existingTag) {
        document.head.removeChild(existingTag);
      }
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h1 className="text-2xl font-bold text-deep-blue mb-6">Datenschutzerklärung</h1>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-deep-blue mb-4">1. Verantwortlicher und Kontakt</h2>
            <p className="mb-4">
              Verantwortlicher i.S.v. Art. 4 Nr. 7 DSGVO:
            </p>
            <p className="mb-4">
              YN Digital GmbH<br />
              Am Schulhof 23<br />
              28865 Lilienthal (nachfolgend: YN Digital oder „wir/uns“) <br />
              info@adressfinder.org
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-deep-blue mb-4">
              2. Kategorien personenbezogener Daten und ihre Herkunft
            </h2>
            <p className="mb-4">
              Wir verarbeiten folgende Kategorien personenbezogener Daten:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Stammdaten (Name, Adresse, Kontaktdaten)</li>
              <li>Vertragsdaten (Kundennr., Anfragenr., Anfragedatum)</li>
              <li>
                Angaben zur gesuchten Person (insbesondere Geschlecht, Name, Geburtsdatum, Adresse) 
                und Auskunftsdaten der Meldebehörden (Statusmeldung (z.B. „nicht ermittelbar“, „Auskunftssperre“ o.ä.), 
                bei Vorliegen der gesetzlichen Voraussetzungen ggf. zusätzlich frühere Namen, Geburtsdatum und Geburtsort 
                sowie bei Geburt im Ausland auch den Staat, Familienstand, derzeitige Staatsangehörigkeiten, frühere 
                Anschriften, Einzugsdatum und Auszugsdatum, Familienname und Vornamen sowie Anschrift des gesetzlichen 
                Vertreters, Familienname und Vornamen sowie Anschrift des Ehegatten oder des Lebenspartners sowie Sterbedatum 
                und Sterbeort sowie bei Versterben im Ausland auch den Staat)
              </li>
              <li>IT-Nutzungsdaten (Zugriffsprotokolle, IP-Adresse, E-Mail-Nutzung und Nutzung der Plattform, Browserdaten etc.)</li>
              <li>Daten, die Sie an uns übermitteln (z.B. per Kontaktformular, per Telefon, per Post)</li>
              <li>Cookies und ähnliche Technologien (dazu unter Ziffer 7)</li>
            </ul>
            <p className="mb-4">
              Wir erheben diese Daten in der Regel direkt; lediglich bei den Angaben zur betroffenen Person werden 
              die personenbezogenen Daten nicht bei diesen, sondern von Dritten – insbesondere den Meldebehörden – erhoben 
              (Art. 14 DSGVO). In letzterem Fall werden die Betroffenen zusätzlich informiert, sofern dies gesetzlich 
              erforderlich ist.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-deep-blue mb-4">
              3. Zwecke und Rechtsgrundlagen der Verarbeitung
            </h2>
            <p className="mb-4">
              Wir verarbeiten personenbezogene Daten zu folgenden Zwecken und auf Basis der folgenden Rechtsgrundlagen:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>Vertragserfüllung, Art. 6 Abs. 1 lit. b) DSGVO:</strong> 
                Insbesondere verarbeiten wir personenbezogene Daten, wenn dies zur Erfüllung des Plattformvertrags erforderlich ist. 
                Dies ist insbesondere der Fall, wenn wir notwendige, vertragliche Leistungen an den Kunden erbringen oder 
                Kundenanfragen zu konkreten (vor)vertraglichen Themen gestellt werden.
              </li>
              <li>
                <strong>Rechtliche Verpflichtung, Art. 6 Abs. 1 lit. c) DSGVO:</strong> 
                Hierunter fällt insbesondere die Verarbeitung personenbezogener Daten zur Erfüllung gesetzlicher Pflichten, 
                wie vor allem aus dem Steuerrecht.
              </li>
              <li>
                <strong>Berechtigte Interessen, Art. 6 Abs. 1 lit. f) DSGVO:</strong> 
                Unsere berechtigten Interessen bestehen insbesondere zur Identitätsprüfung, Adressverifizierung oder zur Erfüllung 
                vertraglicher Pflichten Dritter (insbesondere Erlaubnis zur Einholung einfacher Melderegisterauskünfte gem. 
                §§ 44ff. Bundesmeldegesetz (BMG), die Abfrage bei den Meldebehörden, die Speicherung der personenbezogenen Daten 
                auf unserer Plattform sowie die Weitergabe an die anfragenden natürlichen oder juristischen Personen). 
                Zudem haben wir berechtigte Interessen, unsere Dienste zu verbessern, unsere Kommunikation mit Kunden effizient 
                zu gestalten (z.B. bei Kundenanfragen, die nicht zur Vertragserfüllung erforderlich sind) sowie Marketinginteressen 
                (z.B. beim Newsletterversand gegenüber Bestandskunden i.V.m. § 7 Abs. 3 UWG). 
                Im Einzelfall können personenbezogene Daten zudem bei Unternehmensverkäufen verarbeitet werden, sofern dies 
                erforderlich ist oder wird. Unser berechtigtes Interesse beruht dann in der Durchführung der Unternehmenstransaktion.
              </li>
              <li>
                <strong>Einwilligung, Art. 6 Abs. 1 lit. a) DSGVO:</strong> 
                Dies ist insbesondere der Fall, wenn wir einen Newsletter zusenden (ggf. i.V.m. § 7 Abs. 1 UWG), 
                Analysedienste oder Cookies einsetzen (ggf. i.V.m. § 25 TDDDG), und sofern eine Einwilligung rechtlich erforderlich ist.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-deep-blue mb-4">4. Dauer der Speicherung</h2>
            <p className="mb-4">
              Wir speichern personenbezogene Daten grundsätzlich nur solange, wie dies zur Erfüllung unserer gesetzlichen 
              und vertraglichen Pflichten erforderlich ist.
            </p>
            <p className="mb-4">
              Die Aufbewahrungspflichten richten sich insbesondere nach folgenden Gesetzen:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>
                <strong>Handelsgesetzbuch (HGB):</strong> 6-10 Jahre für Geschäftsunterlagen
              </li>
              <li>
                <strong>Abgabenordnung (AO):</strong> 6-10 Jahre für steuerlich relevante Unterlagen
              </li>
              <li>
                <strong>Bürgerliches Gesetzbuch (BGB):</strong> i.d.R. 3 Jahre allgemeine Verjährungsfrist
              </li>
            </ul>
            <p className="mb-4">
              Berechtigte Interessen zur Aufbewahrung sind insbesondere die Verteidigung von Rechtsansprüchen.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-deep-blue mb-4">5. Empfänger und Weitergabe an Dritte</h2>
            <p className="mb-4">
              An folgende Empfänger oder Kategorien von Empfängern werden Ihre personenbezogenen Daten übermittelt 
              oder können übermittelt werden:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Behörden (insbesondere Finanzämter etc.)</li>
              <li>Steuerberater &amp; Rechtsanwaltskanzleien</li>
              <li>
                Dienstleister im Rahmen des Outsourcings bzw. zur Nutzung von Schnittstellen mit den Meldebehörden 
                (insbesondere RISER ID Services GmbH, Gürtelstr. 30, 10247 Berlin, 
                <a
                  href="https://www.riserid.eu/wissen/datenschutz"
                  className="text-blue-600 hover:underline ml-1"
                  target="_blank" 
                  rel="noreferrer"
                >
                  https://www.riserid.eu/wissen/datenschutz
                </a>)
              </li>
              <li>
                Dienstleister im Rahmen der Zahlungsabwicklung
                (insbesondere Stripe Payments Europe, Limited (SPEL), 1 Grand Canal Street Lower, Grand Canal Dock, Dublin, D02 H210 Irland), 
              </li>
              <li>
                Dienstleister im Rahmen der Identitätsprüfung
                (insbesondere Stripe Payments Europe, Limited (SPEL), 1 Grand Canal Street Lower, Grand Canal Dock, Dublin, D02 H210 Irland), 
              </li>
              <li>
                Cloud-Dienste-/Kollaborations- und IT-Tools/ Customer-Relationship-Management Anbieter 
                (insbesondere HubSpot Ireland Ltd., 2nd Floor 30 North Wall Quay, Dublin 1, Irland)
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-deep-blue mb-4">6. Cookies und ähnliche Technologien</h2>
            <p className="mb-4">
              Unsere Internetseiten verwenden so genannte „Cookies“. Sie werden entweder vorübergehend für die Dauer 
              einer Sitzung (Session-Cookies) oder dauerhaft (permanente Cookies) auf Ihrem Endgerät gespeichert. 
              Session-Cookies werden nach Ende Ihres Besuchs automatisch gelöscht. Permanente Cookies bleiben auf Ihrem 
              Endgerät gespeichert, bis Sie diese selbst löschen oder eine automatische Löschung durch Ihren Webbrowser 
              erfolgt.
            </p>
            <p className="mb-4">
              Cookies können von uns (First-Party-Cookies) oder von Drittunternehmen stammen (sog. Third-Party-Cookies). 
              Third-Party-Cookies ermöglichen die Einbindung bestimmter Dienstleistungen von Drittunternehmen innerhalb 
              von Webseiten (z. B. Cookies zur Abwicklung von Zahlungsdienstleistungen). Cookies haben verschiedene 
              Funktionen. Zahlreiche Cookies sind technisch notwendig, da bestimmte Webseitenfunktionen ohne diese nicht 
              funktionieren würden (z. B. die Warenkorbfunktion oder die Anzeige von Videos). Andere Cookies können zur 
              Auswertung des Nutzerverhaltens oder zu Werbezwecken verwendet werden.
            </p>
            <p className="mb-4">
              Cookies, die zur Durchführung des elektronischen Kommunikationsvorgangs, zur Bereitstellung bestimmter, 
              von Ihnen erwünschter Funktionen (z. B. für die Warenkorbfunktion) oder zur Optimierung der Website 
              (z. B. Cookies zur Messung des Webpublikums) erforderlich sind (notwendige Cookies), werden auf Grundlage 
              von Art. 6 Abs. 1 lit. f DSGVO gespeichert, sofern keine andere Rechtsgrundlage angegeben wird. 
              Der Websitebetreiber hat ein berechtigtes Interesse an der Speicherung von notwendigen Cookies zur 
              technisch fehlerfreien und optimierten Bereitstellung seiner Dienste. Sofern eine Einwilligung zur 
              Speicherung von Cookies und vergleichbaren Wiedererkennungstechnologien abgefragt wurde, erfolgt die 
              Verarbeitung ausschließlich auf Grundlage dieser Einwilligung (Art. 6 Abs. 1 lit. a DSGVO und § 25 Abs. 1 
              TDDDG); die Einwilligung ist jederzeit widerrufbar. Sie können Ihren Browser so einstellen, dass Sie über 
              das Setzen von Cookies informiert werden und Cookies nur im Einzelfall erlauben, die Annahme von Cookies 
              für bestimmte Fälle oder generell ausschließen sowie das automatische Löschen der Cookies beim Schließen 
              des Browsers aktivieren. Bei der Deaktivierung von Cookies kann die Funktionalität dieser Website 
              eingeschränkt sein.
            </p>
            <p className="mb-4">
              Welche Cookies und Dienste auf dieser Website eingesetzt werden, können Sie dieser Datenschutzerklärung 
              entnehmen.
            </p>
            <h3 className="font-semibold mb-2 mt-4">Server-Log-Dateien</h3>
            <p className="mb-4">
              Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten Server-Log-Dateien, 
              die Ihr Browser automatisch an uns übermittelt. Dies sind:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Browsertyp und Browserversion</li>
              <li>verwendetes Betriebssystem</li>
              <li>Referrer URL</li>
              <li>Hostname des zugreifenden Rechners</li>
              <li>Uhrzeit der Serveranfrage</li>
              <li>IP-Adresse</li>
            </ul>
            <p className="mb-4">
              Eine Zusammenführung dieser Daten mit anderen Datenquellen wird nicht vorgenommen.
            </p>
            <p className="mb-4">
              Die Erfassung dieser Daten erfolgt auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO. Der Websitebetreiber 
              hat ein berechtigtes Interesse an der technisch fehlerfreien Darstellung und der Optimierung seiner 
              Website – hierzu müssen die Server-Log-Files erfasst werden.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-deep-blue mb-4">7. Rechte der Betroffenen</h2>
            <p className="mb-4">
              Betroffene haben im Rahmen der gesetzlichen Bestimmungen folgende Rechte:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li>Recht auf Auskunft (Art. 15 DSGVO)</li>
              <li>Recht auf Berichtigung (Art. 16 DSGVO)</li>
              <li>Recht auf Löschung (Art. 17 DSGVO)</li>
              <li>Recht auf Einschränkung (Art. 18 DSGVO)</li>
              <li>Recht auf Datenübertragbarkeit (Art. 20 DSGVO)</li>
              <li>Widerspruchsrecht (Art. 21 DSGVO)</li>
              <li>Recht auf Widerruf der Einwilligung (Art. 7 Abs. 3 DSGVO)</li>
              <li>Beschwerderecht bei einer Aufsichtsbehörde (Art. 77 DSGVO)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-deep-blue mb-4">
              8. Automatisierte Entscheidungsfindung
            </h2>
            <p className="mb-4">
              Eine automatisierte Entscheidungsfindung i.S.v. Art. 22 DSGVO – inklusive Profiling – findet nicht statt.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-semibold text-deep-blue mb-4">9. Änderungen</h2>
            <p className="mb-4">
              Wir aktualisieren diese Datenschutzerklärung regelmäßig.
            </p>
          </section>

          <p className="text-right text-sm text-gray-600 mt-8">Datum: 17.03.2025</p>

          <div className="mt-8">
            <Link to="/" className="text-blue-600 hover:underline">
              Zurück zur Startseite
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DatenschutzNeu;